import React, { useEffect, useRef, useState } from 'react';
import type { IIdleTimer } from 'react-idle-timer';

// Styles
import { darculaSessionTimerStyles } from '../darculaTheme';

import type { ISessionTimerConfig } from '@hooks/useSessionIdle';

type SessionStatus = 'active' | 'prompted' | 'idle' | 'disabled';

interface ISessionTimerInfoProps {
    idleTimer?: IIdleTimer | null;
    sessionConfig?: ISessionTimerConfig;
}

interface ISessionTimerSnapshot {
    remainingMs: number;
    status: SessionStatus;
    lastActive: Date | null;
}

const {
    statusColors,
    rootStyle,
    headerRowStyle,
    titleStyle,
    statusDotStyle,
    countdownRowStyle,
    countdownValueStyle,
    countdownLabelStyle,
    statusTextStyle,
    metaRowStyle,
    metaLineStyle,
    metaLabelStyle,
    metaValueStyle,
} = darculaSessionTimerStyles as {
    statusColors: Record<SessionStatus, string>;
    rootStyle: React.CSSProperties;
    headerRowStyle: React.CSSProperties;
    titleStyle: React.CSSProperties;
    statusDotStyle: React.CSSProperties;
    countdownRowStyle: React.CSSProperties;
    countdownValueStyle: React.CSSProperties;
    countdownLabelStyle: React.CSSProperties;
    statusTextStyle: React.CSSProperties;
    metaRowStyle: React.CSSProperties;
    metaLineStyle: React.CSSProperties;
    metaLabelStyle: React.CSSProperties;
    metaValueStyle: React.CSSProperties;
};

const POLL_INTERVAL_MS = 250;

const STATUS_LABELS: Record<SessionStatus, string> = {
    active: 'Active',
    prompted: 'Warning shown',
    idle: 'Logged out',
    disabled: 'Disabled',
};

const formatDuration = (ms: number): string => {
    const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const formatClock = (date: Date | null): string =>
    date ? date.toLocaleTimeString([], { hour12: false }) : '-';

/**
 * QA-facing live readout of the session-idle timer, toggled from the Ctrl+Shift+1
 * debug header (DebugLabelSelector) and rendered as a fixed overlay. Polls the
 * react-idle-timer instance so testers can reliably watch the countdown, the
 * warning window, and logout.
 */
const SessionTimerInfo = ({ idleTimer, sessionConfig }: ISessionTimerInfoProps) => {
    // idleTimer is a fresh object each render; keep a ref so the poller reads latest.
    const idleTimerRef = useRef<IIdleTimer | null | undefined>(idleTimer);
    idleTimerRef.current = idleTimer;

    const disabled = sessionConfig?.disabled ?? true;

    const [snapshot, setSnapshot] = useState<ISessionTimerSnapshot>({
        remainingMs: 0,
        status: disabled ? 'disabled' : 'active',
        lastActive: null,
    });

    useEffect(() => {
        if (disabled) {
            setSnapshot({ remainingMs: 0, status: 'disabled', lastActive: null });
            return undefined;
        }

        const read = () => {
            const timer = idleTimerRef.current;
            if (!timer) {
                return;
            }

            const remainingMs = timer.getRemainingTime();
            const isIdle = timer.isIdle();
            const isPrompted = timer.isPrompted();
            const status: SessionStatus = isIdle ? 'idle' : isPrompted ? 'prompted' : 'active';
            const lastActive = timer.getLastActiveTime();

            setSnapshot({ remainingMs, status, lastActive });
        };

        read();
        const interval = setInterval(read, POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [disabled]);

    const { remainingMs, status, lastActive } = snapshot;
    const statusColor = statusColors[status] ?? statusColors.active;

    return (
        <div style={rootStyle} data-testid="session-timer-info">
            <div style={headerRowStyle}>
                <p style={titleStyle}>Session Timer</p>
                <span style={{ ...statusDotStyle, backgroundColor: statusColor }} />
            </div>
            <div style={countdownRowStyle}>
                <span
                    style={{ ...countdownValueStyle, color: statusColor }}
                    data-testid="session-timer-countdown"
                >
                    {disabled ? '-' : formatDuration(remainingMs)}
                </span>
                <span style={countdownLabelStyle}>to logout</span>
            </div>
            <div
                style={{ ...statusTextStyle, color: statusColor }}
                data-testid="session-timer-status"
            >
                {STATUS_LABELS[status]}
            </div>
            {!disabled && (
                <div style={metaRowStyle}>
                    <div style={metaLineStyle}>
                        <span style={metaLabelStyle}>Session</span>
                        <span style={metaValueStyle}>{formatDuration(sessionConfig?.timeout ?? 0)}</span>
                    </div>
                    <div style={metaLineStyle}>
                        <span style={metaLabelStyle}>Warning at</span>
                        <span style={metaValueStyle}>{formatDuration(sessionConfig?.promptBeforeIdle ?? 0)} left</span>
                    </div>
                    <div style={metaLineStyle}>
                        <span style={metaLabelStyle}>Last activity</span>
                        <span style={metaValueStyle}>{formatClock(lastActive)}</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SessionTimerInfo;
